'use client';
import { cva, cx } from '../helpers/cva/index.js';
import { createComponent } from '../helpers/create-component.js';
import { forwardRefWithAs } from '../helpers/forward-ref-with-as.js';
import { IconAcceptableValue, IconRenderer } from '../Icon/index.js';
import { Typography } from '../Typography/index.js';
import type { HTMLComponentProps, OverrideProps } from '../../types/helpers.js';
import PlaceholderIcon from '../Icon/icons/PlaceholderIcon/index.js';
import { useBrand } from '../../brand/context/index.js';

export type BannerProps = OverrideProps<
  [
    HTMLComponentProps<'header'>,
    {
      /**
       * An icon to render at the front of the input.
       */
      iconStart?: IconAcceptableValue;
      /**
       * An icon to render to the left of the copy.
       */
      iconPromo?: IconAcceptableValue;
      /**
       * An icon to render at the end of the input.
       */
      iconEnd?: IconAcceptableValue;
    },
  ]
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EmptyIcon = ({ className, ...props }: any) => (
  <PlaceholderIcon className={cx('cur-invisible', className)} {...props} />
);

const getBannerClasses = cva({
  base: 'cur-flex cur-items-center cur-gap-2 cur-p-2',
  variants: {
    brand: {
      curology: 'cur-bg-surface-secondary cur-text-inverse',
      agency: 'cur-bg-surface-secondary cur-text-inverse',
    },
  },
});

/**
 * Banner
 */
export const Banner = createComponent(
  'Banner',
  forwardRefWithAs<'header', BannerProps>(
    (
      {
        children,
        className,
        iconStart = EmptyIcon,
        iconPromo,
        iconEnd = EmptyIcon,
        ...props
      },
      ref,
    ) => {
      const brand = useBrand();

      return (
        <header
          ref={ref}
          className={getBannerClasses({
            brand,
            className,
          })}
          {...props}
        >
          <IconRenderer
            icon={iconStart}
            size="sm"
            className="cur-w-4 cur-shrink-0"
          />
          <div className="cur-flex cur-w-full cur-items-center cur-justify-center">
            <IconRenderer
              icon={iconPromo}
              size="sm"
              className="cur-mr-2 cur-w-4 cur-shrink-0"
            />
            <Typography
              size="meta"
              weight="bold"
              className={cx({ 'cur-mr-6': !!iconPromo })}
            >
              {children}
            </Typography>
          </div>
          <IconRenderer
            icon={iconEnd}
            size="sm"
            className="cur-w-4 cur-shrink-0"
          />
        </header>
      );
    },
  ),
);
